import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import PageContainer from './common/PageContainer';

interface WordTemplate {
  id: string;
  name: string;
  variables: Array<{
    name: string;
    type: string;
    label: string;
    required: boolean;
    maxLength?: number; // 字符限制
    isImage?: boolean; // 是否为图片
    width?: number; // 图片宽度
    height?: number; // 图片高度
  }>;
}

interface TemplatesResponse {
  success: boolean;
  templates: WordTemplate[];
}

// 标准化变量名：去除所有空格
const normalizeVariableName = (name: string): string => {
  return name.replace(/\s+/g, '');
};

const WordPDFCreator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState<WordTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WordTemplate | null>(null);
  const [templateData, setTemplateData] = useState<Record<string, string>>({});
  const [imageFiles, setImageFiles] = useState<Record<string, File>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [outputFormat, setOutputFormat] = useState<'word' | 'pdf'>('word');
  const [isFormValid, setIsFormValid] = useState(false);

  const preselectedTemplateId = searchParams.get('templateId');

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (preselectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === preselectedTemplateId);
      if (template) {
        handleTemplateSelect(template);
      }
    }
  }, [preselectedTemplateId, templates]);

  // 实时验证表单
  useEffect(() => {
    if (!selectedTemplate) {
      setIsFormValid(false);
      return;
    }

    // 检查所有字段是否已填写
    const allFieldsFilled = selectedTemplate.variables.every(variable => {
      const normalizedName = normalizeVariableName(variable.name);
      if (variable.isImage) {
        return !!imageFiles[normalizedName];
      }
      return !!templateData[normalizedName]?.trim();
    });

    // 检查字符限制
    const noOverLimit = selectedTemplate.variables.every(variable => {
      if (variable.isImage) return true;
      
      const normalizedName = normalizeVariableName(variable.name);
      const value = templateData[normalizedName] || '';
      const maxLength = variable.maxLength || 5;
      return value.length <= maxLength;
    });

    setIsFormValid(allFieldsFilled && noOverLimit);
  }, [selectedTemplate, templateData, imageFiles]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/word-templates');
      const data: TemplatesResponse = await response.json();
      
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('获取模板列表失败:', error);
    }
  };

  const handleTemplateSelect = (template: WordTemplate) => {
    setSelectedTemplate(template);
    setError(null);
    setSuccess(null);
    
    const initialData: Record<string, string> = {};
    template.variables.forEach(variable => {
      const normalizedName = normalizeVariableName(variable.name);
      initialData[normalizedName] = '';
    });
    setTemplateData(initialData);
    setImageFiles({}); // 清空图片文件
  };

  const handleDataChange = (variableName: string, value: string, maxLength?: number) => {
    // 标准化变量名
    const normalizedName = normalizeVariableName(variableName);
    
    // 如果设置了字符限制，进行验证
    if (maxLength && value.length > maxLength) {
      // 截断超出的字符
      value = value.substring(0, maxLength);
    }
    
    setTemplateData(prev => ({
      ...prev,
      [normalizedName]: value
    }));
  };

  const handleDateChange = (variableName: string, date: Dayjs | null) => {
    const normalizedName = normalizeVariableName(variableName);
    if (date) {
      setTemplateData(prev => ({
        ...prev,
        [normalizedName]: date.format('YYYY-MM-DD')
      }));
    }
  };

  const handleImageChange = (variableName: string, file: File | null) => {
    const normalizedName = normalizeVariableName(variableName);
    
    if (file) {
      // 验证文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      if (!allowedTypes.includes(file.type)) {
        setError('請上傳有效的圖片文件 (JPG, PNG, GIF, WebP, BMP)');
        return;
      }

      // 验证文件大小（最大 10MB）
      if (file.size > 10 * 1024 * 1024) {
        setError('圖片文件大小不能超過 10MB');
        return;
      }

      setImageFiles(prev => ({
        ...prev,
        [normalizedName]: file
      }));
      
      setError(null);
    } else {
      // 移除图片
      setImageFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[normalizedName];
        return newFiles;
      });
    }
  };

  const validateData = (): boolean => {
    if (!selectedTemplate) return false;
    
    // 1. 检查必填字段
    const requiredFields = selectedTemplate.variables.filter(v => v.required);
    const missingFields = requiredFields.filter(field => {
      const normalizedName = normalizeVariableName(field.name);
      if (field.isImage) {
        return !imageFiles[normalizedName];
      }
      return !templateData[normalizedName]?.trim();
    });
    
    if (missingFields.length > 0) {
      setError(`請填寫必填字段: ${missingFields.map(f => f.label).join(', ')}`);
      return false;
    }
    
    // 2. 检查所有字段是否已填写（全部设为必填）
    const emptyFields = selectedTemplate.variables.filter(variable => {
      const normalizedName = normalizeVariableName(variable.name);
      if (variable.isImage) {
        return !imageFiles[normalizedName];
      }
      return !templateData[normalizedName]?.trim();
    });
    
    if (emptyFields.length > 0) {
      setError(`請填寫所有字段: ${emptyFields.map(f => f.label).join(', ')}`);
      return false;
    }
    
    // 3. 检查字符限制
    const overLimitFields = selectedTemplate.variables.filter(variable => {
      if (variable.isImage) return false; // 图片不检查字符限制
      
      const normalizedName = normalizeVariableName(variable.name);
      const value = templateData[normalizedName] || '';
      const maxLength = variable.maxLength || 5;
      return value.length > maxLength;
    });
    
    if (overLimitFields.length > 0) {
      const details = overLimitFields.map(f => {
        const normalizedName = normalizeVariableName(f.name);
        const value = templateData[normalizedName] || '';
        const maxLength = f.maxLength || 5;
        return `${f.label} (${value.length}/${maxLength})`;
      }).join(', ');
      setError(`以下字段超過字符限制: ${details}`);
      return false;
    }
    
    return true;
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !validateData()) {
      return;
    }

    setIsGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = outputFormat === 'pdf' 
        ? `/api/word-templates/${selectedTemplate.id}/generate-pdf`
        : `/api/word-templates/${selectedTemplate.id}/generate`;

      // 使用 FormData 来支持文件上传
      const formData = new FormData();
      
      // 添加文本数据
      formData.append('data', JSON.stringify(templateData));
      
      // 添加图片文件
      Object.entries(imageFiles).forEach(([variableName, file]) => {
        formData.append(`image_${variableName}`, file);
      });

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData
        // 不设置 Content-Type，让浏览器自动设置 multipart/form-data
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const extension = outputFormat === 'pdf' ? 'pdf' : 'docx';
        link.download = `${selectedTemplate.name}-${timestamp}.${extension}`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccess(`${outputFormat === 'pdf' ? 'PDF' : 'Word'}文檔生成成功！`);
      } else {
        const errorData = await response.text();
        throw new Error(errorData || '生成失敗');
      }

    } catch (error) {
      console.error('生成文檔失敗:', error);
      setError(error instanceof Error ? error.message : '生成失敗，請重試');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderVariableInput = (variable: { name: string; type: string; label: string; required: boolean; maxLength?: number; isImage?: boolean; width?: number; height?: number }) => {
    const { name, type, label, required, maxLength, isImage, width, height } = variable;
    const normalizedName = normalizeVariableName(name);
    const value = templateData[normalizedName] || '';
    
    // 如果是图片类型
    if (isImage && type === 'image') {
      const selectedFile = imageFiles[normalizedName];
      
      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {label} {required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            變量名: {'{{' + name + '}}'}  | 目標尺寸: {width}x{height} 像素
          </Typography>
          
          <Box sx={{ 
            border: 2, 
            borderStyle: 'dashed',
            borderColor: selectedFile ? 'success.main' : 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: selectedFile ? 'success.lighter' : 'background.paper',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
          onClick={() => document.getElementById(`image-input-${name}`)?.click()}
          >
            {selectedFile ? (
              <Box>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  大小: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageChange(name, null);
                    }}
                  >
                    移除圖片
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  點擊上傳圖片
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  支持 JPG, PNG, GIF, WebP, BMP | 最大 10MB<br/>
                  圖片會自動<strong>強制縮放</strong>到 {width}x{height} 像素（不保持比例）
                </Typography>
              </Box>
            )}
            
            <input
              id={`image-input-${name}`}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImageChange(name, file);
                }
              }}
              style={{ display: 'none' }}
            />
          </Box>
        </Box>
      );
    }
    
    // 计算字符数量和显示提示
    const charCount = value.length;
    const maxLengthValue = maxLength || 5; // 默认5个字符
    const isOverLimit = charCount > maxLengthValue;
    
    // 生成帮助文本
    const getHelperText = () => {
      const baseText = `變量名: {{${name}}}`;
      const limitText = ` | 字符限制: ${charCount}/${maxLengthValue}`;
      return baseText + limitText;
    };

    switch (type) {
    case 'date':
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={label + (required ? ' *' : '')}
            value={value ? dayjs(value) : null}
            onChange={(date) => handleDateChange(name, date)}
            slotProps={{
              textField: {
                fullWidth: true,
                required: required,
                helperText: `變量名: {{${name}}} | 字符限制: ${maxLengthValue}`
              }
            }}
          />
        </LocalizationProvider>
      );
      
    case 'number':
      return (
        <TextField
          fullWidth
          type="number"
          label={label}
          value={value}
          onChange={(e) => handleDataChange(name, e.target.value, maxLengthValue)}
          required={required}
          variant="outlined"
          error={isOverLimit}
          helperText={getHelperText()}
          inputProps={{ maxLength: maxLengthValue }}
        />
      );
      
    case 'email':
      return (
        <TextField
          fullWidth
          type="email"
          label={label}
          value={value}
          onChange={(e) => handleDataChange(name, e.target.value, maxLengthValue)}
          required={required}
          variant="outlined"
          error={isOverLimit}
          helperText={getHelperText()}
          inputProps={{ maxLength: maxLengthValue }}
        />
      );
      
    case 'tel':
      return (
        <TextField
          fullWidth
          type="tel"
          label={label}
          value={value}
          onChange={(e) => handleDataChange(name, e.target.value, maxLengthValue)}
          required={required}
          variant="outlined"
          error={isOverLimit}
          helperText={getHelperText()}
          inputProps={{ maxLength: maxLengthValue }}
        />
      );
      
    default:
      if (label.includes('内容') || label.includes('描述') || label.includes('说明')) {
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            label={label}
            value={value}
            onChange={(e) => handleDataChange(name, e.target.value, maxLengthValue)}
            required={required}
            variant="outlined"
            error={isOverLimit}
            helperText={getHelperText()}
            inputProps={{ maxLength: maxLengthValue }}
          />
        );
      }
        
      return (
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={(e) => handleDataChange(name, e.target.value, maxLengthValue)}
          required={required}
          variant="outlined"
          error={isOverLimit}
          helperText={getHelperText()}
          inputProps={{ maxLength: maxLengthValue }}
        />
      );
    }
  };

  return (
    <PageContainer maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          創建PDF文檔
        </Typography>
        <Typography variant="body1" color="text.secondary">
          選擇Word模板並填寫內容來生成PDF文檔
        </Typography>
      </Box>

      {/* 模板選擇 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. 選擇模板
        </Typography>
        {templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              還沒有可用的模板
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/create-template')}
              sx={{ mt: 2 }}
            >
              創建第一個模板
            </Button>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2 
          }}>
            {templates.map((template) => (
              <Card 
                key={template.id}
                variant={selectedTemplate?.id === template.id ? 'outlined' : 'elevation'}
                sx={{
                  border: selectedTemplate?.id === template.id ? 2 : 0,
                  borderColor: 'primary.main',
                  backgroundColor: selectedTemplate?.id === template.id ? 'action.selected' : 'background.paper'
                }}
              >
                <CardActionArea onClick={() => handleTemplateSelect(template)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DescriptionIcon sx={{ mr: 1 }} color="primary" />
                      <Typography variant="h6" component="div">
                        {template.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {template.variables.length} 個變量
                    </Typography>
                    {selectedTemplate?.id === template.id && (
                      <Chip 
                        icon={<CheckCircleIcon />}
                        label="已選擇" 
                        color="primary" 
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      {/* 數據填寫 */}
      {selectedTemplate && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            2. 填寫內容
          </Typography>
          
          {/* 輸出格式選擇 */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              📄 輸出格式選擇
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>格式保持現實情況</strong>
              <Typography variant="body2">
                複雜Word文檔轉PDF時，格式保持是技術難題。我們提供最佳解決方案：
              </Typography>
            </Alert>
            
            <FormControl component="fieldset">
              <RadioGroup
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value as 'word' | 'pdf')}
              >
                <Paper elevation={2} sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'success.main' }}>
                  <FormControlLabel 
                    value="word" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Word文檔 (.docx)
                          </Typography>
                          <Chip label="強烈推薦" color="success" size="small" sx={{ ml: 1 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          ✅ 100% 完美格式保持<br/>
                          ✅ 表格邊框、顏色、佈局完整<br/>
                          ✅ 支持後續編輯和修改
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
                
                <Paper elevation={2} sx={{ p: 2, borderLeft: 4, borderColor: 'warning.main' }}>
                  <FormControlLabel 
                    value="pdf" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            PDF文檔 (.pdf)
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          ⚠️ 格式保持有限制<br/>
                          ✅ 便攜分享，通用查看<br/>
                          ⚠️ 複雜表格可能變成簡單文本
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              </RadioGroup>
            </FormControl>
            
            <Alert severity="success" icon={<WarningIcon />} sx={{ mt: 2 }}>
              <strong>💡 專業建議：</strong>
              對於包含複雜表格、顏色、佈局的重要文檔，<strong>強烈建議選擇Word格式</strong>以確保100%格式完整性。
            </Alert>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 變量表單 */}
          <Stack spacing={3}>
            {selectedTemplate.variables.map((variable) => (
              <Box key={variable.name}>
                {renderVariableInput(variable)}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* 錯誤和成功消息 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* 生成按鈕 */}
      {selectedTemplate && (
        <Box sx={{ textAlign: 'center', pb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleGenerate}
            disabled={isGenerating || !isFormValid}
            startIcon={isGenerating ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 200 }}
          >
            {isGenerating ? '生成中...' : `生成${outputFormat === 'pdf' ? 'PDF' : 'Word'}文檔`}
          </Button>
          {!isFormValid && !isGenerating && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              請填寫所有字段並確保符合字符限制
            </Typography>
          )}
        </Box>
      )}
    </PageContainer>
  );
};

export default WordPDFCreator;
